// Sugalarm — kiosk OTA context bridge wrapper (cycle 225).
//
// The single owner of access to the native `KioskOta` plugin, which exists
// ONLY in the Android kiosk APK (the `kiosk` product flavor; cycle-225 build
// or later). It is absent on web, the Pi kiosk, iPhone, the phone layout,
// and the `standard` Android flavor — so `isAvailable()` is false everywhere
// except the kiosk APK and every call is an inert no-op elsewhere.
//
// The native OTA self-updater can't read the WebView's localStorage (where
// the device token lives) or the SPA's alarm-episode / route state, so the
// SPA pushes a small context snapshot here periodically. Native uses it for
// the device-token-authed heartbeat AND — crucially — the `alarmActive` flag
// is the alarm-safe install gate: the updater NEVER installs while it holds.
// Cycle 311 (operator policy: "Lows kill - Highs do not"): the flag is
// LOW-SIDE-scoped — `episode.isLive` AND the episode level is `low` /
// `urgent_low`. A high-side episode no longer defers installs; low-side
// episodes can NEVER be bypassed (the cycle-310 cross-check can clear only
// a latch the server itself says is over).
//
// Reached through the bridge-injected `window.Capacitor.Plugins` proxy
// (cycle 26/138 rule) — NEVER a bare `import("@capacitor/...")`. Mirrors the
// `lib/kioskWifi.ts` pattern.

import { isCapacitorNative } from "./capacitor";

export interface KioskOtaContext {
  token: string | null;
  route: string;
  lastReadingAt: string | null;
  lastAlarmAt: string | null;
  alarmActive: boolean;
  online: boolean;
  /** Cycle 310: true while alarm AUDIO is actually sounding
   *  (`episode.audioOn`) — one of the three conditions the native
   *  deferral cross-check requires false before it may install past a
   *  stale `alarmActive`. A pre-310 shell ignores it. */
  audioActive: boolean;
  /** Cycle 473.1: true while an alarm COUNTS by the cycle-361 predicate
   *  (`lib/alarmOutranksOverlay.ts`: audio on at any level, or a low-side
   *  episode live, muted or not; a Stopped or snoozed HIGH does not; the
   *  no-data cue is not an episode). Read ONLY by the native Wi-Fi screens'
   *  rule and its foreign-prompt bounce (the operator, 2026-11-03: the
   *  system Wi-Fi dialog or panel opens only if no alarm has counted in the
   *  last 10 minutes) — never by the OTA gate, which stays low-side. An
   *  older APK ignores it. Omitted = the native side reads "live" (unknown
   *  is not quiet). */
  episodeLive?: boolean;
  /** Cycle 310: the LAST wedge event (a latched-past-recovery episode
   *  the reconciliation cleared) — relayed into the native heartbeat so
   *  fleet recurrence shows in the admin roster. Null until one occurs. */
  lastWedgeAtMs: number | null;
  lastWedgeEpisodeAgeSeconds: number | null;
  lastWedgeActiveMemberNull: boolean | null;
  /** Cycle 471: `true` tells the native updater to DROP the device token it
   *  holds, so the heartbeat stops. `OtaUpdater.reportContext` has always
   *  ignored an empty `token` (a transient null must not stop a monitoring
   *  screen's heartbeat), which is why a kiosk kept heartbeating with its
   *  old, now-revoked token after a sign-out until its process restarted.
   *  A FIELD, not a new plugin method: an old APK ignores it silently and
   *  behaves exactly as before. Omitted = false. */
  clearToken?: boolean;
}

interface KioskOtaProxy {
  reportContext(ctx: KioskOtaContext): Promise<void>;
  checkNow(): Promise<void>;
}

function plugin(): KioskOtaProxy | null {
  // `typeof window` first: `isCapacitorNative()` checks it too, but a test
  // double of `./capacitor` may answer true in node, and this module must
  // stay inert there rather than throw into a fire-and-forget caller.
  if (typeof window === "undefined") return null;
  if (!isCapacitorNative()) return null;
  const cap = (
    window as { Capacitor?: { Plugins?: Record<string, unknown> } }
  ).Capacitor;
  const p = cap?.Plugins?.KioskOta;
  return p != null ? (p as KioskOtaProxy) : null;
}

/** True iff the native KioskOta plugin is registered on the bridge — i.e.
 *  the SPA is running inside the Android kiosk APK (cycle-225 build+). */
export function isAvailable(): boolean {
  return plugin() != null;
}

/** Push the SPA's live context to the native OTA updater. No-op (and never
 *  throws) when the plugin isn't present. */
export async function reportContext(ctx: KioskOtaContext): Promise<void> {
  const p = plugin();
  if (!p) return;
  try {
    await p.reportContext(ctx);
  } catch {
    // Telemetry/context only — never surface a thrown bridge error.
  }
}

/** Cycle 471 — after an EXPLICIT kiosk sign-out, tell the native updater to
 *  drop the device token it holds so its heartbeat stops presenting a
 *  credential the server has just revoked. Called by `teardownAccount`
 *  AFTER `clearAll()` (the server-side self-revoke has already run or timed
 *  out by then). No-op off the kiosk APK; on an old APK the field is
 *  ignored and the heartbeat stops at the next process restart or OTA (the
 *  server refuses the dead token loudly, cycle 432). Never throws. */
export async function clearToken(): Promise<void> {
  try {
    const p = plugin();
    if (!p) return;
    await p.reportContext({
      token: null,
      clearToken: true,
      route: "/login",
      lastReadingAt: null,
      lastAlarmAt: null,
      alarmActive: false,
      audioActive: false,
      episodeLive: false,
      online: typeof navigator !== "undefined" ? navigator.onLine : true,
      lastWedgeAtMs: null,
      lastWedgeEpisodeAgeSeconds: null,
      lastWedgeActiveMemberNull: null,
    });
  } catch {
    // Best effort — the local teardown has already completed.
  }
}
